import { createAsyncThunk } from '@reduxjs/toolkit';

import * as jobApi from '../api/job.api';
import { startLoading, stopLoading } from '@/store/loaderSlice';

import type {
  CreateJobPayload,
  UpdateJobPayload,
  JobFilters,
  Job,
  JobApprovalRequest,
} from '../types/job';

interface JobsResponse {
  jobs: Job[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

const getErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  if (
    typeof error === 'object' &&
    error !== null
  ) {
    const err = error as {
      response?: {
        data?: {
          message?: string;
        };
      };
      message?: string;
      error?: string;
    };

    return (
      err.response?.data?.message ||
      err.message ||
      err.error ||
      fallback
    );
  }

  return fallback;
};

export const fetchJobs = createAsyncThunk<
  JobsResponse,
  JobFilters,
  {
    rejectValue: string;
  }
>(
  'jobs/fetchJobs',
  async (
    filters,
    thunkAPI
  ) => {
    try {
      return await jobApi.getJobs(
        filters
      );
    } catch (error) {
      return thunkAPI.rejectWithValue(
        getErrorMessage(
          error,
          'Failed to fetch jobs'
        )
      );
    }
  }
);

export const fetchDashboardJobs =
  createAsyncThunk<
    JobsResponse,
    JobFilters,
    {
      rejectValue: string;
    }
  >(
    'jobs/fetchDashboardJobs',
    async (
      filters,
      thunkAPI
    ) => {
      try {
        return await jobApi.getDashboardJobs(
          filters
        );
      } catch (error) {
        return thunkAPI.rejectWithValue(
          getErrorMessage(
            error,
            'Failed to fetch dashboard jobs'
          )
        );
      }
    }
  );

export const fetchJobById =
  createAsyncThunk<
    Job,
    string,
    {
      rejectValue: string;
    }
  >(
    'jobs/fetchJobById',
    async (
      jobId,
      thunkAPI
    ) => {
      try {
        return await jobApi.getJobById(
          jobId
        );
      } catch (error) {
        return thunkAPI.rejectWithValue(
          getErrorMessage(
            error,
            'Failed to fetch job'
          )
        );
      }
    }
  );


export const createJob =
  createAsyncThunk<
    Job | JobApprovalRequest,
    CreateJobPayload,
    {
      rejectValue: string;
    }
  >(
    'jobs/createJob',
    async (
      payload,
      thunkAPI
    ) => {
      thunkAPI.dispatch(startLoading());

      try {
        return await jobApi.createJob(payload);
      } catch (error) {
        return thunkAPI.rejectWithValue(
          getErrorMessage(
            error,
            'Failed to create job'
          )
        );
      } finally {
        thunkAPI.dispatch(stopLoading());
      }
    }
  );



export const updateJob =
  createAsyncThunk<
    Job | JobApprovalRequest,
    {
      jobId: string;
      payload: UpdateJobPayload;
    },
    {
      rejectValue: string;
    }
  >(
    'jobs/updateJob',
    async (
      {
        jobId,
        payload,
      },
      thunkAPI
    ) => {
      thunkAPI.dispatch(startLoading());

      try {
        return await jobApi.updateJob(
          jobId,
          payload
        );
      } catch (error) {
        return thunkAPI.rejectWithValue(
          getErrorMessage(
            error,
            'Failed to update job'
          )
        );
      } finally {
        thunkAPI.dispatch(stopLoading());
      }
    }
  );



export const updateJobStatus =
  createAsyncThunk<
    Job,
    {
      jobId: string;
      status: string;
    },
    {
      rejectValue: string;
    }
  >(
    'jobs/updateJobStatus',
    async (
      {
        jobId,
        status,
      },
      thunkAPI
    ) => {
      thunkAPI.dispatch(startLoading());

      try {
        return await jobApi.updateJobStatus(
          jobId,
          status
        );
      } catch (error) {
        return thunkAPI.rejectWithValue(
          getErrorMessage(
            error,
            'Failed to update job status'
          )
        );
      } finally {
        thunkAPI.dispatch(stopLoading());
      }
    }
  );

export const deleteJob =
  createAsyncThunk<
    string,
    string,
    {
      rejectValue: string;
    }
  >(
    'jobs/deleteJob',
    async (
      jobId,
      thunkAPI
    ) => {
      try {
        await jobApi.deleteJob(
          jobId
        );

        return jobId;
      } catch (error) {
        return thunkAPI.rejectWithValue(
          getErrorMessage(
            error,
            'Failed to delete job'
          )
        );
      }
    }
  );

